import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { ToastOptions, toast } from "react-toastify";

type RetryAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

interface TokenRefreshResponse {
  access: string;
  refresh?: string;
}

const TOAST_DEDUPE_DURATION = 3500;

let refreshTokenRequest: Promise<TokenRefreshResponse> | null = null;
const latestToastByKey = new Map<string, number>();

const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh");
};

const showErrorToast = (message: string, options?: ToastOptions) => {
  const toastKey = `error:${message}`;
  const now = Date.now();
  const latestToastAt = latestToastByKey.get(toastKey);

  if (
    toast.isActive(toastKey) ||
    (latestToastAt && now - latestToastAt < TOAST_DEDUPE_DURATION)
  ) {
    return;
  }

  latestToastByKey.set(toastKey, now);
  toast.error(message, {
    toastId: toastKey,
    ...options,
    onClose: (...args) => {
      latestToastByKey.delete(toastKey);
      options?.onClose?.(...args);
    },
  });
};

const getTokenRefreshResponse = async (): Promise<TokenRefreshResponse> => {
  const refresh = localStorage.getItem("refresh");

  if (!refresh) {
    throw new Error("Refresh token not found");
  }

  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/v1/auth/token/refresh/`,
    { refresh }
  );

  return response.data?.data || response.data;
};

const refreshToken = async () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = getTokenRefreshResponse().finally(() => {
      refreshTokenRequest = null;
    });
  }

  return refreshTokenRequest;
};

export const responseErrorInterceptor = async (error: unknown) => {
  if (error instanceof AxiosError) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response?.data?.errors?.[0]?.detail;
      const originalRequest = error.config as RetryAxiosRequestConfig | undefined;

      switch (status) {
        case 401: {
          if (originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
              const data = await refreshToken();

              localStorage.setItem("token", data.access);

              if (data.refresh) {
                localStorage.setItem("refresh", data.refresh);
              }

              originalRequest.headers.set("Authorization", `Bearer ${data.access}`);

              return axios(originalRequest);
            } catch {
              clearAuth();
            }
          } else {
            clearAuth();
          }

          showErrorToast(message || "شما نشست معتبری ندارید", {
            onClose: () => {
              window.dispatchEvent(new Event("unauthorized"));
            },
          });
          break;
        }

        case 403: {
          clearAuth();
          showErrorToast(message || "شما دسترسی لازم را ندارید", {
            onClose: () => {
              window.dispatchEvent(new Event("unauthorized"));
            },
          });
          break;
        }

        case 404: {
          showErrorToast(message || "مورد مورد نظر پیدا نشد");
          break;
        }

        default: {
          showErrorToast(message || "خطای ناشناخته از سرور");
        }
      }
    } else if (error.request) {
      showErrorToast("خطا در ارتباط با سرور");
    } else {
      showErrorToast("خطای غیرمنتظره در درخواست");
    }
  } else {
    showErrorToast("خطای ناشناخته");
  }

  return Promise.reject(error);
};
